import classnames from 'classnames'
import AgentTypingIndicator from 'components/AgentTypingIndicator'
import LoaderBalls from 'components/LoaderBalls'
import MessageGroup from 'components/MessageGroup'
import useFetchActiveConversationMessages from 'hooks/useFetchActiveConversationMessages'
import useIsMobile from 'hooks/useIsMobile'
import usePrevious from 'hooks/usePrevious'
import useScroll from 'hooks/useScroll'
import useThemeColors from 'hooks/useThemeColors'
import { markConversationAsRead } from 'modules/conversations'
import { getActiveConversationId } from 'modules/conversations/selectors'
import DateTimeManager from 'modules/dateTime'
import { isEndUserMessage } from 'modules/message/utils'
import { autoScrollChatToBottom } from 'modules/view/actions'
import { last, omit, pathOr } from 'ramda'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars'
import { useDispatch, useSelector } from 'react-redux'
import { ConversationId } from 'types/conversation'
import { Message as MessageModel, ViewerRecipientStatus } from 'types/message'
import { delay } from 'utils'

import './style.css'

const shouldShowTimestamp = (previousGroup, currentGroup, showTimestampForFirstGroup = true) => {
  if(!previousGroup && !showTimestampForFirstGroup) {
    return false
  }

  if (!previousGroup) {
    return true
  }

  const previousTime = previousGroup ? previousGroup[previousGroup.length - 1].createdAt : 0
  return DateTimeManager.isTimeAfterHours(currentGroup[0].createdAt, previousTime, 2)
}

const setScrollTop = ($scrollContainer, scrollTop, isMobile) => {
  if ($scrollContainer.scrollTop !== scrollTop) {
    const style = $scrollContainer.style

    if (isMobile) {
      // For mobile WebKit below iOS 13, we need to disable gesture scrolling before setting the scrollTop
      // See: https://popmotion.io/blog/20170704-manually-set-scroll-while-ios-momentum-scroll-bounces/
      // With iOS 13 this no longer works as it is enabled by default, so overflow instead
      // See "Removed Features": https://developer.apple.com/documentation/safari_release_notes/safari_13_release_notes
      style['-webkit-overflow-scrolling'] = 'auto'
      style['overflow'] = 'hidden'
    }

    $scrollContainer.scrollTop = scrollTop

    if (isMobile) {
      style['overflow'] = 'scroll'
      style['-webkit-overflow-scrolling'] = 'touch'
    }
  }
}

interface MessageListProps {
  reflowHidden: boolean,
  messageGroups: MessageModel[][],
  messages: MessageModel[],
  disabled?: boolean,
  inheritBackgroundStyle?: boolean,
  showTimestampForFirstGroup?: boolean
}

const MessageGroupList = ({
  reflowHidden,
  messageGroups,
  messages,
  disabled = false,
  inheritBackgroundStyle = false,
  showTimestampForFirstGroup = true
}: MessageListProps) => {
  const dispatch = useDispatch()
  const { widgetBackgroundStyles } = useThemeColors()
  const activeConversation: ConversationId = useSelector(getActiveConversationId)
  const { fetchMessages } = useFetchActiveConversationMessages()
  const { chatOpen: open, shouldAutoScrollChatToBottom } = useSelector(state => state.view)
  const { hasMoreMessages, nextPageMessageId } = useSelector(state => (
    pathOr({}, ['conversations', 'conversations', activeConversation], state)
  ))
  const isMobile = useIsMobile()

  const [initialLoad, setInitialLoad] = useState(true)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  const [isLoaderVisible, setIsLoaderVisible] = useState(false)
  const [lastLoadFromScroll, setLastLoadFromScroll] = useState(false)
  const scrollContainerRef = useRef(null)
  const prevScrollHeightRef = useRef(0)
  const scrollAfterOpenRef = useRef(false)

  const latestMessage = useMemo(() => last(messages), [messages])
  const prevLatestMessage = usePrevious(latestMessage)

  const hasNewMessage = !!prevLatestMessage && latestMessage !== prevLatestMessage
  const rowCount = messageGroups.length
  const canStillFetch = !isLoadingMessages && hasMoreMessages && open

  const renderedMessageGroups = useMemo(() => (
    messageGroups.map((messageGroup, index) => {
      const isLastGroup = index === messageGroups.length - 1
      const previousGroup = index > 0 ? messageGroups[index - 1] : null
      const messageGroupId = messageGroup[0].attributes.widgetGuid || messageGroup[0].id
      return (
        <React.Fragment key={`messageGroup-${messageGroupId}`}>
          <MessageGroup
            role="listitem"
            messages={messageGroup}
            isLastGroup={isLastGroup}
            showTimestamp={shouldShowTimestamp(previousGroup, messageGroup, showTimestampForFirstGroup)}
          />
          {isLastGroup && <AgentTypingIndicator />}
        </React.Fragment>
      )
    })
  ), [messageGroups, showTimestampForFirstGroup])

  const doMessageFetch = useCallback(async (nextId) => {
    setIsLoadingMessages(true)
    setIsLoaderVisible(true)
    await fetchMessages({
      from: nextId
    })
    setLastLoadFromScroll(true)
    setIsLoadingMessages(false)
    delay(() => setIsLoaderVisible(false), 1000)
  }, [fetchMessages])

  const onThrottledScroll = useCallback((event) => {
    if (event.target.scrollTop <= 200 && canStillFetch && !lastLoadFromScroll) {
      doMessageFetch(nextPageMessageId)
    }
  }, [doMessageFetch, nextPageMessageId, canStillFetch, lastLoadFromScroll])

  const backgroundStyles = inheritBackgroundStyle ? {} : widgetBackgroundStyles

  useScroll(onThrottledScroll, scrollContainerRef)

  useLayoutEffect(() => {
    if (rowCount < 1) {
      return
    }

    if (open && !isLoadingMessages && !reflowHidden) {
      const scrollHeight = pathOr<number>(0, ['current', 'scrollHeight'], scrollContainerRef)
      const prevScrollHeight = prevScrollHeightRef.current

      const shouldScrollToBottom = hasNewMessage || initialLoad || scrollAfterOpenRef.current || shouldAutoScrollChatToBottom

      if (scrollContainerRef.current) {
        if (shouldScrollToBottom) {
          setScrollTop(scrollContainerRef.current, scrollHeight, isMobile)
          scrollAfterOpenRef.current = false
        } else if (lastLoadFromScroll) {
          setScrollTop(scrollContainerRef.current, scrollHeight - prevScrollHeight, isMobile)
        }
      }

      if (shouldScrollToBottom || lastLoadFromScroll) {
        prevScrollHeightRef.current = scrollHeight
      }

      setInitialLoad(false)
      setLastLoadFromScroll(false)
    } else if (!open && hasNewMessage) {
      scrollAfterOpenRef.current = true
    }
    if (shouldAutoScrollChatToBottom){
      dispatch(autoScrollChatToBottom(false))
    }
  }, [rowCount, open, initialLoad, isLoadingMessages, hasNewMessage, lastLoadFromScroll, reflowHidden, isMobile, messageGroups, shouldAutoScrollChatToBottom, dispatch])

  useEffect(() => {
    setLastLoadFromScroll(false)
    setInitialLoad(true)
  }, [activeConversation])

  useEffect(() => {
    if (!latestMessage || !open) {
      return
    }

    const isUnreadAgentMessage = latestMessage.viewerRecipientStatus !== ViewerRecipientStatus.READ
      && !isEndUserMessage(latestMessage)

    if (isUnreadAgentMessage) {
      markConversationAsRead(activeConversation)
    }

  }, [latestMessage, activeConversation, open])

  const renderScrollbarsView = props => {
    // This property gets passed along from the Scrollbars
    // Instead, we'll control -webkit-overflow-scrolling style
    // with CSS and during scrollTop updates
    const style = omit(['WebkitOverflowScrolling'], props.style)

    return (
      <div
        {...props}
        role="list"
        style={style}
        className={classnames(
          'drift-widget-message-group-list',
          { 'drift-widget-message-group-list--hidden': reflowHidden }
        )}
      />
    )
  }

  const setScrollContainerRef = (scrollbarsRef) => {
    if (scrollbarsRef && scrollContainerRef) {
      scrollContainerRef.current = scrollbarsRef.view
    }
  }

  return (
    <div className="drift-widget-message-group-list-container" aria-hidden={disabled}>
      <div
        style={backgroundStyles}
        className={classnames("drift-widget-message-group-list-loader", {
          'drift-widget-message-group-list-loader--loading': isLoaderVisible
        })}
      >
        <LoaderBalls visible={isLoaderVisible} />
      </div>
      <Scrollbars
        ref={setScrollContainerRef}
        hideTracksWhenNotNeeded
        renderView={renderScrollbarsView}
      >
        {renderedMessageGroups}
      </Scrollbars>
    </div>
  )
}

export default MessageGroupList
